// client/src/components/wholesaler/BillingHistory.js
// Billing transaction history table component

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const BillingHistory = ({ transactions }) => {
  const { t } = useTranslation(['wholesaler']);
  const [filter, setFilter] = useState('all'); // all, per_order, subscription, adjustment

  const formatCurrency = (amount) => {
    return `€${parseFloat(amount).toFixed(2)}`;
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const getFeeTypeLabel = (feeType) => {
    const labels = {
      per_order: t('subscription.billing.feeTypes.perOrder', 'Per-Order Fee'),
      subscription: t('subscription.billing.feeTypes.subscription', 'Subscription Fee'),
      adjustment: t('subscription.billing.feeTypes.adjustment', 'Adjustment')
    };
    return labels[feeType] || feeType;
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    const labels = {
      pending: t('subscription.billing.status.pending', 'Pending'),
      completed: t('subscription.billing.status.completed', 'Completed'),
      cancelled: t('subscription.billing.status.cancelled', 'Cancelled')
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${badges[status]}`}>
        {labels[status]}
      </span>
    );
  };

  const filteredTransactions = transactions.filter(t => {
    if (filter === 'all') return true;
    return t.feeType === filter;
  });

  // Group transactions by month
  const groupedByMonth = filteredTransactions.reduce((acc, transaction) => {
    const date = new Date(transaction.transactionDate);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const monthLabel = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    
    if (!acc[monthKey]) {
      acc[monthKey] = {
        label: monthLabel,
        transactions: [],
        total: 0
      };
    }
    
    acc[monthKey].transactions.push(transaction);
    acc[monthKey].total += transaction.amount;
    
    return acc;
  }, {});

  // Sort months in descending order
  const sortedMonths = Object.entries(groupedByMonth).sort((a, b) => b[0].localeCompare(a[0]));

  if (!transactions || transactions.length === 0) {
    return (
      <div className="text-center py-12">
        <div className="text-gray-400 text-5xl mb-4">📄</div>
        <h3 className="text-lg font-semibold text-gray-700 mb-2">
          {t('subscription.billing.noTransactions', 'No Transactions Yet')}
        </h3>
        <p className="text-gray-500">
          {t('subscription.billing.noTransactionsDesc', 'Your billing history will appear here once you start receiving orders')}
        </p>
      </div>
    );
  }

  return (
    <div>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'all'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('subscription.billing.filters.all', 'All')} ({transactions.length})
        </button>
        <button
          onClick={() => setFilter('per_order')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'per_order'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('subscription.billing.filters.perOrder', 'Per-Order Fees')}
        </button>
        <button
          onClick={() => setFilter('subscription')}
          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            filter === 'subscription'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
          }`}
        >
          {t('subscription.billing.filters.subscription', 'Subscription Fees')}
        </button>
      </div>

      {/* Summary Stats */}
      <div className="grid md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">
            {t('subscription.billing.totalTransactions', 'Total Transactions')}
          </div>
          <div className="text-2xl font-bold text-gray-800">
            {filteredTransactions.length}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">
            {t('subscription.billing.totalAmount', 'Total Amount')}
          </div>
          <div className="text-2xl font-bold text-gray-800">
            {formatCurrency(filteredTransactions.reduce((sum, t) => sum + t.amount, 0))}
          </div>
        </div>
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="text-sm text-gray-600 mb-1">
            {t('subscription.billing.averageTransaction', 'Average Transaction')}
          </div>
          <div className="text-2xl font-bold text-gray-800">
            {formatCurrency(filteredTransactions.reduce((sum, t) => sum + t.amount, 0) / filteredTransactions.length || 0)}
          </div>
        </div>
      </div>

      {/* Transactions by Month */}
      <div className="space-y-6">
        {sortedMonths.map(([monthKey, monthData]) => (
          <div key={monthKey} className="border border-gray-200 rounded-lg overflow-hidden">
            {/* Month Header */}
            <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
              <h3 className="font-semibold text-gray-800">{monthData.label}</h3>
              <span className="text-sm font-medium text-gray-600">
                {t('subscription.billing.monthTotal', 'Total')}: {formatCurrency(monthData.total)}
              </span>
            </div>

            {/* Transactions Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('subscription.billing.table.date', 'Date')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('subscription.billing.table.type', 'Type')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('subscription.billing.table.description', 'Description')}
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('subscription.billing.table.status', 'Status')}
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('subscription.billing.table.amount', 'Amount')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {monthData.transactions.map((transaction) => (
                    <tr key={transaction._id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {formatDate(transaction.transactionDate)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-700">
                        {getFeeTypeLabel(transaction.feeType)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {transaction.orderId?.orderNumber 
                          ? `${t('subscription.billing.order', 'Order')} #${transaction.orderId.orderNumber}`
                          : transaction.notes || '-'
                        }
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm">
                        {getStatusBadge(transaction.status)}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 text-right">
                        {formatCurrency(transaction.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      {filteredTransactions.length === 0 && filter !== 'all' && (
        <div className="text-center py-12">
          <p className="text-gray-500">
            {t('subscription.billing.noTransactionsFilter', 'No transactions found for this filter')}
          </p>
        </div>
      )}
    </div>
  );
};

export default BillingHistory;